// KISSED site-content defaults for About page, public-page headers, and
// global nav/footer. Consumed by public pages (published) and the Website
// Manager (draft). Section-level shallow merge over these defaults.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

export const DEFAULT_ABOUT = {
  hero: {
    visible: true,
    eyebrow: "Our Philosophy",
    headline: "The scalp is an extension of the body.",
    subheadline: "Hair loss is rarely just about hair. It's a signal — of stress, of gut health, of hormones, of inflammation, of styling habits, of life. At KISSED, care addresses both the internal root causes and the external scalp environment, together.",
    image: "",
  },
  founder: {
    visible: true,
    kicker: "Meet Dr. Christy Vonta Bell, DTS, HHP",
    name: "Founder & Lead Practitioner, KISSED Functional Wellness",
    subtitle: "Trichologist · Holistic Health Practitioner · Herbalist · Educator",
    image: "",
    paragraphs: [
      "For nearly 30 years, Dr. Christy Vonta Bell has worked at the intersection of hair, scalp, beauty, education, wellness, and human behavior. What began as a career in the beauty industry evolved into a much deeper pursuit: understanding why changes happen — not simply finding another product to cover the symptoms.",
      "Her approach is grounded in the understanding that hair and scalp concerns don't always exist in isolation. Stress, nutrition, lifestyle, life transitions, wellness patterns, hair-care practices, environmental factors, and underlying medical concerns can all be important pieces of a much larger picture.",
      "Her role is to investigate the pieces within her professional scope, identify observable patterns, help clients better understand their findings, develop appropriate wellness and scalp-care recommendations, and recognize when collaboration or referral to a licensed medical provider is warranted.",
    ],
    quote: "What is your hair, scalp, and body trying to tell us?",
  },
  credentials: {
    visible: true,
    heading: "Credentials",
    items: [
      { label: "Doctor of Trichology Science (DTS)", visible: true },
      { label: "Board-Certified Trichologist", visible: true },
      { label: "Board-Certified Holistic Health Practitioner", visible: true },
      { label: "Master Herbalist", visible: true },
      { label: "Licensed Cosmetologist", visible: true },
      { label: "Licensed Educator", visible: true },
      { label: "Doctoral Studies in Psychology", visible: true },
      { label: "Nearly 30 Years of Professional Experience in Technology, Education & Wellness", visible: true },
    ],
  },
  cta: {
    visible: true,
    heading: "Ready to begin?",
    copy: "Start with the KISSED Hair Loss Investigation, or explore wellness assessments if hair isn't your primary concern.",
    primaryLabel: "Start with Hair Loss Investigation",
    primaryTo: "/hair-scalp",
    secondaryLabel: "Explore Wellness Assessments",
    secondaryTo: "/wellness",
  },
};

export const DEFAULT_PAGES = {
  services: {
    visible: true,
    eyebrow: "In-Clinic Services",
    heading: "When Hands-On Care Makes Sense.",
    subcopy: "Your journey can begin virtually. When hands-on support is the right next step, these in-person services are available at our Raleigh, NC clinic.",
    bookLabel: "Book an In-Person Service",
    existingHeading: "Existing Client Services",
    existingCopy: "KISSED continues to offer a limited selection of healthy-hair maintenance, loc, alopecia-camouflage and wax services for existing clients.",
  },
  programs: {
    visible: true,
    eyebrow: "Programs",
    heading: "Structured Support, Built Around Your Findings.",
    subcopy: "KISSED programs follow your own assessment findings and history — personalized guidance over a focused program window.",
  },
  shop: {
    visible: true,
    eyebrow: "KISSED Shop",
    heading: "Functional Formulas & Wellness Products.",
    subcopy: "Practitioner-selected herbal formulas, teas, tinctures and hair & scalp essentials to support your plan.",
  },
};

export const DEFAULT_SITE = {
  nav: {
    items: [
      { label: "Hair & Scalp", to: "/hair-scalp", visible: true },
      { label: "Virtual Assessments & Wellness", to: "/wellness", visible: true },
      { label: "In-Clinic Services", to: "/in-clinic-services", visible: true },
      { label: "Shop", to: "/shop", visible: true },
      { label: "About", to: "/about", visible: true },
      { label: "Client Care Hub", to: "/client/login", visible: true },
    ],
    ctaLabel: "New Client Assessment",
    ctaTo: "/navigator",
  },
  footer: {
    description: "Virtual-first functional wellness & trichology. In-clinic services available in Raleigh, NC.",
    contactEmail: "",
    contactPhone: "",
    copyright: "KISSED Trichology & Functional Wellness Clinic · Raleigh, NC",
    disclaimer: "KISSED Functional Wellness does not diagnose, cure, or treat medical conditions. Wellness assessments identify observable patterns and offer personalized, non-medical recommendations, with referral to licensed medical providers when appropriate.",
    socials: [],
    legal: [],
  },
};

export function mergeContent(defaults, published) {
  const out = { ...defaults };
  if (published && typeof published === "object") {
    for (const k of Object.keys(defaults)) {
      if (published[k] && typeof published[k] === "object") out[k] = published[k];
    }
  }
  return out;
}

// Fetch published (or draft when ?preview=1) content for a page, merged over defaults.
export function useSiteContent(page, defaults) {
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [content, setContent] = useState(defaults);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const url = preview ? `/site-content/admin/${page}?mode=draft` : `/site-content/${page}`;
        const { data } = await api.get(url);
        if (active) setContent(mergeContent(defaults, data.content));
      } catch { if (active) setContent(defaults); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, preview]);
  return content;
}
